/** P32 (Feb 27, 2026) — Previous claims side-by-side comparison dialog.
 *
 *  Shown when the biller clicks "View/Recycle Previous Claim" — lets
 *  them eyeball the most recent 1-2 production claims for the same
 *  patient + service before keying a new one (catches accidental
 *  duplicates + lets recurring weekly claims rehydrate from history).
 */
import React from 'react';
import { formatDateMDY } from '../utils/dates';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Card, CardContent } from '../../../components/ui/card';

// Each claim status gets its own badge palette — centralising the map
// here makes it easy to extend with new statuses (e.g. Denied).
const CLAIM_STATUS_BADGE_CLASSES = {
  Paid: 'bg-green-100 text-green-800',
  Submitted: 'bg-blue-100 text-blue-800',
};
const defaultClaimStatusBadgeClass = 'bg-slate-100 text-slate-700';
const claimStatusBadgeClass = (status) =>
  CLAIM_STATUS_BADGE_CLASSES[status] || defaultClaimStatusBadgeClass;

function PreviousClaimDialog({ open, onClose, claims }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" data-testid="previous-claim-dialog">
        <DialogHeader>
          <DialogTitle>Previous claims for this patient + service</DialogTitle>
          <DialogDescription>
            Side-by-side comparison of the {claims.length} most recent production claim{claims.length === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>
        {claims.length === 0 ? (
          <p className="text-sm text-slate-500 italic" data-testid="previous-claim-empty">No previous production claims found for this patient/service combination.</p>
        ) : (
          <div className={`grid gap-4 ${claims.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {claims.map((c, i) => (
              <Card key={c.id} data-testid={`previous-claim-card-${i}`}>
                <CardContent className="pt-6 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold">{c.claim_number}</span>
                    <span className={`px-2 py-0.5 text-xs rounded ${claimStatusBadgeClass(c.status)}`}>{c.status}</span>
                  </div>
                  <div className="text-slate-500">{formatDateMDY(c.service_date)}</div>
                  <div className="border-t pt-2 space-y-1">
                    <div><strong>Service:</strong> {c.service_code} {c.modifier_1 ? `· ${c.modifier_1}` : ''}</div>
                    <div><strong>Payer:</strong> {c.payer_type} · {c.payer_program || '—'}</div>
                    <div><strong>Dx:</strong> {c.diagnosis_code || '—'}</div>
                    <div><strong>Units:</strong> {c.units}</div>
                    <div><strong>Total:</strong> ${Number(c.total_charge || 0).toFixed(2)}</div>
                    {c.patient_responsibility ? (
                      <div className="text-amber-700"><strong>Patient resp.:</strong> ${Number(c.patient_responsibility || 0).toFixed(2)}</div>
                    ) : null}
                    {c.net_claim_amount ? (
                      <div><strong>Net payout:</strong> ${Number(c.net_claim_amount || 0).toFixed(2)}</div>
                    ) : null}
                    {c.prior_authorization_number ? (
                      <div><strong>PAR:</strong> {c.prior_authorization_number}</div>
                    ) : null}
                    {(c.service_lines && c.service_lines.length > 0) ? (
                      <div>
                        <strong>Lines:</strong>
                        <ul className="ml-4 list-disc text-xs">
                          {c.service_lines.map((sl, k) => (
                            <li key={`${sl.service_date || 'na'}-${sl.units || 0}-${k}`}>
                              {formatDateMDY(sl.service_date)} · {sl.units}u
                              {sl.modifier_1 ? ` · mod ${sl.modifier_1}` : ' · bare'}
                              {sl.charge ? ` · $${Number(sl.charge).toFixed(2)}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PreviousClaimDialog;
